import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bookmark,
  Building2,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { AssignmentBar } from '@/components/AssignmentBar';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Page } from '@/components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Label,
  LoadingState,
  Select,
  Separator,
  Spinner,
} from '@/components/ui/primitives';
import {
  ConfirmButton,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
} from '@/components/ui/overlays';
import { LeadFormDialog } from '@/components/LeadFormDialog';
import { CountyPicker } from '@/components/CountyPicker';
import { STATE_NAMES, SUPPORTED_STATES } from '@shared/counties/index.js';
import {
  cn,
  formatPhone,
  pipelineStatusClass,
  scoreClass,
  severityClass,
  webPresenceClass,
} from '@/lib/utils';
import { useIsEnlarged } from '@/lib/useUiScale';
import { PIPELINE_STATUSES, WEBSITE_CONDITIONS, WEB_PRESENCE_STATUSES } from '@shared/enums.js';
import type { LeadFilter } from '@shared/schemas.js';
import { OpportunityFilter } from '@/components/OpportunityFilter';
import { OPPORTUNITY_LABELS, IMPROVEMENT_LABELS } from '@shared/opportunities.js';

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Date added' },
  { value: 'updatedAt', label: 'Last updated' },
  { value: 'businessName', label: 'Business name' },
  { value: 'normalizedLeadScore', label: 'Lead score' },
  { value: 'rating', label: 'Rating' },
  { value: 'reviewCount', label: 'Review count' },
  { value: 'lastContactedAt', label: 'Last contacted' },
  { value: 'city', label: 'City' },
  { value: 'businessStartedDate', label: 'Newest business' },
] as const;

export function LeadsPage() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [filter, setFilter] = React.useState<LeadFilter>({});
  const [searchInput, setSearchInput] = React.useState('');
  const [sortBy, setSortBy] = React.useState<string>('createdAt');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(50);
  /*
    The filter rail starts open where there is room for it and closed where there
    is not: at 100%/110% a 1440px viewport shows the rail and a usable table at
    once, and at 125%/140% the same rail costs the table the columns that matter.
    A default, not a lock — the Filters button toggles it at any scale, and the
    active filters stay visible as chips while it is closed.
  */
  const enlarged = useIsEnlarged();
  const [showFilters, setShowFilters] = React.useState(!enlarged);
  const [selected, setSelected] = React.useState<Set<number>>(new Set());
  const [allMatchingFilter, setAllMatchingFilter] = React.useState<string | null>(null);
  const filterKey = JSON.stringify(filter);
  const allMatchingSelected = allMatchingFilter === filterKey;
  const clearSelection = () => { setSelected(new Set()); setAllMatchingFilter(null); };
  React.useEffect(() => {
    setSelected(new Set());
    setAllMatchingFilter(null);
  }, [filterKey]);
  const { user, authEnabled } = useAuth();
  // A VA has no admin controls. The server refuses each of these anyway; this
  // stops the screen being covered in buttons that only produce a 403.
  const isVa = authEnabled && user?.role === 'va';
  const [newLeadOpen, setNewLeadOpen] = React.useState(!isVa && params.get('new') === '1');
  const [saveFilterOpen, setSaveFilterOpen] = React.useState(false);

  // Debounce the search box so typing does not fire a query per keystroke.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setFilter((f) => ({ ...f, search: searchInput.trim() || undefined }));
      setPage(1);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    if (params.get('new') === '1') {
      setNewLeadOpen(true);
      params.delete('new');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const query = { filter, sortBy: sortBy as never, sortDir, page, pageSize };
  const leads = useQuery({
    queryKey: ['leads', query],
    queryFn: () => api.listLeads(query),
    placeholderData: keepPreviousData,
  });

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.categories });
  const counties = useQuery({ queryKey: ['counties'], queryFn: api.counties });
  const savedFilters = useQuery({ queryKey: ['saved-filters'], queryFn: api.savedFilters });

  const exportMutation = useMutation({
    mutationFn: () => api.exportLeads({ ...query, pageSize: 500, page: 1 }),
    onSuccess: () => toast.success('Export downloaded'),
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteSavedFilter = useMutation({
    mutationFn: (id: number) => api.deleteSavedFilter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-filters'] });
      toast.success('Saved filter removed');
    },
  });

  const activeFilterCount = countActiveFilters(filter);
  const rows = leads.data?.rows ?? [];
  const selectionCount = allMatchingSelected ? (leads.data?.total ?? 0) : selected.size;
  const selectionDisabled = leads.isFetching || !!leads.error || searchInput.trim() !== (filter.search ?? '');
  const allSelected = rows.length > 0 && (allMatchingSelected || rows.every((r) => selected.has(r.id)));

  const toggleAll = () => {
    if (allMatchingSelected) { clearSelection(); return; }
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  const patchFilter = (patch: Partial<LeadFilter>) => {
    setFilter((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  return (
    <Page
      title="Leads"
      description={
        leads.data
          ? `${leads.data.total.toLocaleString()} matching ${leads.data.total === 1 ? 'lead' : 'leads'}`
          : undefined
      }
      noScroll
      actions={
        <>
          {!isVa && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportMutation.mutate()}
                loading={exportMutation.isPending}
              >
                <Download className="h-3.5 w-3.5" />
                Export CSV
              </Button>
              <Button size="sm" onClick={() => setNewLeadOpen(true)}>
                <Plus className="h-3.5 w-3.5" />
                New lead
              </Button>
            </>
          )}
        </>
      }
    >
      {/*
        The approved layout: a left filter rail beside the table, 15rem when open
        and a 2.25rem icon rail when closed. Laid out with explicit grid
        placement so each block sits where it belongs regardless of source order
        — the rail spans the full height on the left, everything else stacks in
        the right-hand column.
      */}
      <div
        className={cn(
          'grid min-h-0 flex-1 grid-rows-[auto_auto_minmax(0,1fr)_auto] p-5',
          showFilters ? 'grid-cols-[15rem_minmax(0,1fr)] gap-3' : 'grid-cols-[2.25rem_minmax(0,1fr)] gap-2',
        )}
      >
        {/* ---- Collapsed rail: one click reopens it ---- */}
        {!showFilters && (
          <div className="col-start-1 row-span-4 row-start-1 flex min-h-0 flex-col items-center gap-2 rounded-md border border-border bg-surface py-2">
            <Tooltip content="Show filters">
              <button
                onClick={() => setShowFilters(true)}
                aria-label="Show filters"
                className={cn(
                  'relative flex h-7 w-7 items-center justify-center rounded border transition-colors',
                  activeFilterCount > 0
                    ? 'border-primary/40 bg-primary/15 text-primary'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                <Filter className="h-3.5 w-3.5" />
                {activeFilterCount > 0 && (
                  <span className="absolute -right-1 -top-1 rounded-full bg-primary px-1 text-[0.5625rem] font-semibold leading-[0.875rem] text-primary-foreground">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </Tooltip>
            <span
              className="text-2xs font-medium tracking-wide text-muted-foreground"
              style={{ writingMode: 'vertical-rl' }}
            >
              Filters
            </span>
          </div>
        )}

        {/* ---- Toolbar ---- */}
        <div className="col-start-2 row-start-1 mb-3 flex shrink-0 flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-2.5 shadow-sm">
          <div className="relative min-w-[18rem] flex-1 max-w-md">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search names, notes, addresses — or type digits for a phone lookup"
              className="pl-7"
            />
            {searchInput && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchInput('')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <Button
            variant={showFilters || activeFilterCount > 0 ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowFilters((v) => !v)}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
            {activeFilterCount > 0 && <Badge variant="primary">{activeFilterCount}</Badge>}
          </Button>

          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-40">
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                Sort: {o.label}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Owner name"
            value={filter.hasOwnerName === undefined ? '' : String(filter.hasOwnerName)}
            onChange={(e) => patchFilter({ hasOwnerName: e.target.value === '' ? undefined : e.target.value === 'true' })}
            className="w-44"
          >
            <option value="">Owner: All leads</option>
            <option value="true">With owner name</option>
            <option value="false">Without owner name</option>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </Button>

          {leads.isFetching && <Spinner />}

          <div className="ml-auto flex items-center gap-2">
            {!isVa && !allMatchingSelected && (
              <Button variant="outline" size="sm" disabled={selectionDisabled || !leads.data?.total}
                onClick={() => { setSelected(new Set()); setAllMatchingFilter(filterKey); }}>
                Select all {leads.data?.total.toLocaleString() ?? ''} matching
              </Button>
            )}
            {selectionCount > 0 && (
              <>
                <span aria-live="polite" className="text-xs text-muted-foreground">{selectionCount.toLocaleString()} selected{allMatchingSelected ? ' across all pages' : ''}</span>
                <Button variant="ghost" size="sm" onClick={clearSelection}>
                  Clear
                </Button>
              </>
            )}
            {!isVa && <AssignmentBar
              selectedIds={[...selected]}
              filter={filter}
              matchingCount={leads.data?.total ?? 0}
              allMatchingSelected={allMatchingSelected}
              disabled={selectionDisabled}
              onDone={clearSelection}
            />}
          </div>
        </div>

        {/*
          ---- Active filters, while the rail is closed ----
          Closing the rail hides the controls, never the fact that the list is
          filtered. Each chip clears its own filter.
        */}
        {!showFilters && activeFilterCount > 0 && (
          <div className="col-start-2 row-start-2 flex shrink-0 flex-wrap items-center gap-1.5 pb-2">
            <span className="text-2xs uppercase tracking-wide text-muted-foreground">
              Filtered by
            </span>
            {describeActiveFilters(filter, {
              categories: categories.data,
              counties: counties.data,
            }).map((chip) => (
              <button
                key={chip.key}
                onClick={() => patchFilter({ [chip.key]: undefined } as Partial<LeadFilter>)}
                className="inline-flex items-center gap-1 rounded border border-primary/40 bg-primary/15 px-1.5 py-0.5 text-2xs font-medium text-primary transition-colors hover:border-primary/70"
                title={`Clear ${chip.label}`}
              >
                {chip.label}
                <X className="h-3 w-3" />
              </button>
            ))}
            <Button variant="ghost" size="sm" onClick={() => setFilter({ search: filter.search })}>
              Clear all
            </Button>
          </div>
        )}

        {/* ---- Filter rail ---- */}
        {showFilters && (
          <aside className="col-start-1 row-span-4 row-start-1 flex min-h-0 flex-col overflow-y-auto rounded-xl border border-border bg-card p-4 shadow-sm">
            <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
              <span className="section-label">Filters</span>
              <Tooltip content="Hide filters">
                <button
                  onClick={() => setShowFilters(false)}
                  aria-label="Hide filters"
                  className="flex h-5 w-5 items-center justify-center rounded border border-border text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ChevronLeft className="h-3 w-3" />
                </button>
              </Tooltip>
            </div>
            {/* One column: every production filter, stacked in a 15rem rail. */}
            <div className="grid grid-cols-1 gap-3">
              <OpportunityFilter filter={filter} onChange={patchFilter} />
              <Field label="Pipeline status">
                <Select
                  value={filter.pipelineStatuses?.[0] ?? ''}
                  onChange={(e) =>
                    patchFilter({ pipelineStatuses: e.target.value ? [e.target.value as never] : undefined })
                  }
                >
                  <option value="">Any</option>
                  {PIPELINE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Category">
                <Select
                  value={filter.categoryIds?.[0] ?? ''}
                  onChange={(e) =>
                    patchFilter({ categoryIds: e.target.value ? [Number(e.target.value)] : undefined })
                  }
                >
                  <option value="">Any</option>
                  {categories.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="State">
                <Select
                  value={filter.stateCodes?.length === 1 ? filter.stateCodes[0] : ''}
                  onChange={(e) =>
                    patchFilter({ stateCodes: e.target.value ? [e.target.value as 'OH' | 'MI'] : undefined })
                  }
                >
                  <option value="">All states</option>
                  {SUPPORTED_STATES.map((code) => (
                    <option key={code} value={code}>
                      {STATE_NAMES[code]}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="County">
                <CountyPicker
                  multiple
                  counties={counties.data}
                  value={filter.countyIds ?? []}
                  onChange={(ids) => patchFilter({ countyIds: ids.length ? ids : undefined })}
                  placeholder="Any county"
                />
              </Field>

              {/*
                Physical is the default and means what countyIds has always
                meant, so every saved filter keeps its current results.
              */}
              <Field label="County match">
                <Select
                  value={filter.countyMode ?? 'physical'}
                  onChange={(e) =>
                    patchFilter({
                      countyMode:
                        e.target.value === 'physical'
                          ? undefined
                          : (e.target.value as 'served' | 'either'),
                    })
                  }
                >
                  <option value="physical">Located in</option>
                  <option value="served">Serves</option>
                  <option value="either">Located in or serves</option>
                </Select>
              </Field>

              <Field label="Web presence">
                <Select
                  value={filter.webPresenceStatuses?.[0] ?? ''}
                  onChange={(e) =>
                    patchFilter({ webPresenceStatuses: e.target.value ? [e.target.value as never] : undefined })
                  }
                >
                  <option value="">Any</option>
                  {WEB_PRESENCE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Website condition">
                <Select
                  value={filter.websiteConditions?.[0] ?? ''}
                  onChange={(e) =>
                    patchFilter({ websiteConditions: e.target.value ? [e.target.value as never] : undefined })
                  }
                >
                  <option value="">Any</option>
                  {WEBSITE_CONDITIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="City">
                <Input
                  value={filter.cities?.[0] ?? ''}
                  onChange={(e) => patchFilter({ cities: e.target.value ? [e.target.value] : undefined })}
                  placeholder="Any"
                />
              </Field>

              <Field label="Min rating">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  value={filter.minRating ?? ''}
                  onChange={(e) => patchFilter({ minRating: e.target.value ? Number(e.target.value) : undefined })}
                />
              </Field>

              <Field label="Min reviews">
                <Input
                  type="number"
                  min="0"
                  value={filter.minReviewCount ?? ''}
                  onChange={(e) =>
                    patchFilter({ minReviewCount: e.target.value ? Number(e.target.value) : undefined })
                  }
                />
              </Field>

              <Field label="Min score">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={filter.minScore ?? ''}
                  onChange={(e) => patchFilter({ minScore: e.target.value ? Number(e.target.value) : undefined })}
                />
              </Field>

              <Field label="Business age" hint="Only businesses younger than this.">
                <Select
                  value={filter.maxBusinessAgeMonths ? String(filter.maxBusinessAgeMonths) : ""}
                  onChange={(e) =>
                    patchFilter({
                      maxBusinessAgeMonths: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                >
                  <option value="">Any age</option>
                  <option value="12">Under 1 year</option>
                  <option value="24">Under 2 years</option>
                  <option value="36">Under 3 years</option>
                  <option value="48">Under 4 years</option>
                </Select>
              </Field>

              <Field label="Follow-up state">
                <Select
                  value={filter.followUpState ?? 'any'}
                  onChange={(e) =>
                    patchFilter({ followUpState: e.target.value === 'any' ? undefined : (e.target.value as never) })
                  }
                >
                  <option value="any">Any</option>
                  <option value="none">No open follow-up</option>
                  <option value="open">Has open follow-up</option>
                  <option value="overdue">Overdue</option>
                  <option value="due_today">Due today</option>
                </Select>
              </Field>

              <Field label="Website findings">
                <Select
                  value={
                    filter.brokenFormOnly
                      ? 'form'
                      : filter.indexingProblemOnly
                        ? 'indexing'
                        : filter.mobileProblemOnly
                          ? 'mobile'
                          : filter.hasOpenWebsiteIssues
                            ? 'any'
                            : ''
                  }
                  onChange={(e) =>
                    patchFilter({
                      hasOpenWebsiteIssues: e.target.value === 'any' ? true : undefined,
                      brokenFormOnly: e.target.value === 'form' ? true : undefined,
                      indexingProblemOnly: e.target.value === 'indexing' ? true : undefined,
                      mobileProblemOnly: e.target.value === 'mobile' ? true : undefined,
                    })
                  }
                >
                  <option value="">Any</option>
                  <option value="any">Has open findings</option>
                  <option value="form">Broken form</option>
                  <option value="indexing">Indexing problem</option>
                  <option value="mobile">Mobile layout problem</option>
                </Select>
              </Field>

              <div className="space-y-1.5 pt-4">
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={!!filter.hasPhone}
                    onCheckedChange={(v) => patchFilter({ hasPhone: v ? true : undefined })}
                  />
                  Has phone
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={!!filter.ownerVerified}
                    onCheckedChange={(v) => patchFilter({ ownerVerified: v ? true : undefined })}
                  />
                  Owner confirmed by BBB
                </label>
              </div>

              <div className="space-y-1.5 pt-4">
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={!!filter.includeArchived}
                    onCheckedChange={(v) => patchFilter({ includeArchived: v ? true : undefined })}
                  />
                  Include archived
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={!!filter.includeDnc}
                    onCheckedChange={(v) => patchFilter({ includeDnc: v ? true : undefined })}
                  />
                  Include Do Not Contact
                </label>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilter({});
                  setSearchInput('');
                  setPage(1);
                }}
              >
                Clear all
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSaveFilterOpen(true)}>
                <Bookmark className="h-3.5 w-3.5" />
                Save this filter
              </Button>

              {savedFilters.data && savedFilters.data.length > 0 && (
                <>
                  <Separator orientation="vertical" className="mx-1 h-5" />
                  <span className="section-label">Saved</span>
                  <div className="flex flex-wrap items-center gap-1">
                    {savedFilters.data.map((sf) => (
                      <span key={sf.id} className="group inline-flex items-center">
                        <button
                          onClick={() => {
                            setFilter(sf.filter);
                            setSearchInput(sf.filter.search ?? '');
                            if (sf.sortBy) setSortBy(sf.sortBy);
                            if (sf.sortDir) setSortDir(sf.sortDir as 'asc' | 'desc');
                            setPage(1);
                          }}
                          className="rounded-l border border-border bg-surface-raised px-2 py-0.5 text-2xs hover:bg-accent"
                        >
                          {sf.name}
                        </button>
                        <ConfirmButton
                          title={`Delete “${sf.name}”?`}
                          description="The saved filter is removed. Leads are not affected."
                          confirmLabel="Delete"
                          onConfirm={() => deleteSavedFilter.mutate(sf.id)}
                        >
                          <button className="rounded-r border border-l-0 border-border bg-surface-raised px-1 py-0.5 text-muted-foreground hover:bg-destructive/20 hover:text-red-300">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </ConfirmButton>
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </aside>
        )}

        {/* ---- Table ---- */}
        <div className="col-start-2 row-start-3 min-h-0 overflow-auto rounded-xl border border-border bg-card shadow-sm">
          {leads.isLoading ? (
            <LoadingState />
          ) : leads.error ? (
            <ErrorState error={leads.error} onRetry={() => leads.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="No leads match"
              description={
                activeFilterCount > 0 || filter.search
                  ? 'Try clearing a filter or widening the search.'
                  : isVa
                    ? 'Leads assigned to you will appear here.'
                    : 'Import a list or create a lead to get started.'
              }
              action={
                isVa ? undefined : (
                  <Button size="sm" onClick={() => setNewLeadOpen(true)}>
                    <Plus className="h-3.5 w-3.5" />
                    New lead
                  </Button>
                )
              }
            />
          ) : (
            <table className={cn('data-table', enlarged && '[&_th]:px-2 [&_td]:px-2')}>
              <thead>
                {/*
                  Exactly the nine approved columns, at every scale: checkbox,
                  Business, Location, Category, Phone, Web, Score, Status,
                  Assignee. Owner, business age, rating, call count and last
                  contact are all on the lead's own page; carrying them here cost
                  the columns people actually scan by.
                */}
                <tr>
                  <th className="w-8">
                    <Checkbox aria-label="Select this page" checked={allSelected} disabled={selectionDisabled} onCheckedChange={toggleAll} />
                  </th>
                  {/* Business absorbs the leftover width; the rest collapse to content. */}
                  <th className="w-full min-w-[14rem]">Business</th>
                  <th className="w-32">Location</th>
                  <th className="w-28">Category</th>
                  <th className="w-36">Phone</th>
                  <th className="w-32">Web</th>
                  <th className="w-16 text-right">Score</th>
                  <th className="w-32">Status</th>
                  <th className="w-28">Assignee</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((lead) => (
                  <tr key={lead.id} data-selected={allMatchingSelected || selected.has(lead.id)}>
                    <td>
                      <Checkbox
                        aria-label={`Select ${lead.businessName}`}
                        disabled={selectionDisabled}
                        checked={allMatchingSelected || selected.has(lead.id)}
                        onCheckedChange={(v) => {
                          setAllMatchingFilter(null);
                          setSelected((prev) => {
                            const next = allMatchingSelected ? new Set(rows.map(row => row.id)) : new Set(prev);
                            if (v) next.add(lead.id);
                            else next.delete(lead.id);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5">
                        <Link
                          to={`/leads/${lead.id}`}
                          className="truncate font-bold tracking-tight hover:text-primary"
                        >
                          {lead.businessName}
                        </Link>
                        {lead.doNotContact && <Badge variant="danger">DNC</Badge>}
                          <Badge variant="outline">{OPPORTUNITY_LABELS[lead.opportunityType??'unclassified']}</Badge>
                        {lead.archivedAt && <Badge>Archived</Badge>}
                        {lead.hasCustomer && <Badge variant="success">Customer</Badge>}
                        {lead.openFollowUpCount > 0 && (
                          <Tooltip content={`${lead.openFollowUpCount} open follow-up(s)`}>
                            <Badge variant="primary">{lead.openFollowUpCount} FU</Badge>
                          </Tooltip>
                        )}
                        {lead.openWebsiteIssueCount ? (
                          <Tooltip content={`${lead.openWebsiteIssueCount} open website findings`}>
                            <Badge className={cn('border', severityClass(lead.highestOpenIssueSeverity))}>
                              {lead.openWebsiteIssueCount}
                            </Badge>
                          </Tooltip>
                        ) : null}
                      </div>
                    </td>
                    <td className="truncate text-muted-foreground">
                      {[lead.city, lead.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="truncate text-muted-foreground">{lead.categoryName ?? '—'}</td>
                    <td>
                      <span className="whitespace-nowrap font-mono text-sm tabular-nums">
                        {formatPhone(lead.primaryPhone)}
                      </span>
                    </td>
                    <td>
                      <Badge className={cn('border', webPresenceClass(lead.webPresenceStatus))}>
                        {lead.webPresenceStatus}
                      </Badge>
                    </td>
                    <td className={cn('text-right font-semibold tabular-nums', scoreClass(lead.normalizedLeadScore))}>
                      {lead.normalizedLeadScore ?? '—'}
                    </td>
                    <td>
                      <Badge className={cn('border', pipelineStatusClass(lead.pipelineStatus))}>
                        {lead.pipelineStatus}
                      </Badge>
                    </td>
                    <td className="truncate text-muted-foreground">{lead.assigneeName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ---- Pagination ---- */}
        {leads.data && leads.data.total > 0 && (
          <div className="col-start-2 row-start-4 flex shrink-0 items-center justify-between gap-3 pt-2 text-xs">
            <div className="flex items-center gap-2 text-muted-foreground">
              <span>
                {((page - 1) * pageSize + 1).toLocaleString()}–
                {Math.min(page * pageSize, leads.data.total).toLocaleString()} of{' '}
                {leads.data.total.toLocaleString()}
              </span>
              <Select
                value={String(pageSize)}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="h-6 w-20 text-2xs"
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)}>
                First
              </Button>
              <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="px-2 tabular-nums">
                {page} / {leads.data.pageCount}
              </span>
              <Button
                variant="outline"
                size="icon"
                disabled={page >= leads.data.pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= leads.data.pageCount}
                onClick={() => setPage(leads.data!.pageCount)}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>

      <LeadFormDialog open={newLeadOpen} onOpenChange={setNewLeadOpen} />
      <SaveFilterDialog
        open={saveFilterOpen}
        onOpenChange={setSaveFilterOpen}
        filter={filter}
        sortBy={sortBy}
        sortDir={sortDir}
      />
    </Page>
  );
}

function isActiveFilterEntry([key, value]: [string, unknown]): boolean {
  if (key === 'search') return false;
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function countActiveFilters(filter: LeadFilter): number {
  return Object.entries(filter).filter(isActiveFilterEntry).length;
}

/** Readable names for the filter keys. Anything unlisted falls back to its key. */
const FILTER_LABELS: Record<string, string> = {
  opportunityTypes:'Opportunity type',
  improvementCategories:'Improvement category',
  pipelineStatuses: 'Status',
  categoryIds: 'Category',
  countyIds: 'County',
  serviceCountyIds: 'Serves county',
  countyMode: 'County match',
  stateCodes: 'State',
  cities: 'City',
  webPresenceStatuses: 'Web presence',
  websiteConditions: 'Website condition',
  minRating: 'Min rating',
  maxRating: 'Max rating',
  minReviewCount: 'Min reviews',
  minScore: 'Min score',
  maxScore: 'Max score',
  hasOwnerName: 'Has owner name',
  ownerVerified: 'Owner confirmed by BBB',
  hasPhone: 'Has phone',
  hasEmail: 'Has email',
  minAttemptCount: 'Min calls',
  maxAttemptCount: 'Max calls',
  lastAttemptBefore: 'Called before',
  lastAttemptAfter: 'Called after',
  importedAfter: 'Imported after',
  importedBefore: 'Imported before',
  importId: 'Import',
  followUpState: 'Follow-ups',
  cooldownEligibleOnly: 'Off cooldown',
  includeArchived: 'Including archived',
  includeDnc: 'Including DNC',
  onlyDnc: 'DNC only',
  onlyArchived: 'Archived only',
  hasOpenWebsiteIssues: 'Has website findings',
  minOpenIssueSeverity: 'Min severity',
  websiteIssueTypeCodes: 'Finding type',
  brokenFormOnly: 'Broken form',
  indexingProblemOnly: 'Indexing problem',
  mobileProblemOnly: 'Mobile problem',
  hasCustomer: 'Is a customer',
  maxBusinessAgeMonths: 'Younger than (months)',
  hasBusinessStartDate: 'Has start date',
};

/**
 * The active filters, as short readable chips.
 *
 * Shown whenever the rail is closed, which is the default at 125%/140%. A
 * collapsed rail must never mean hidden filtering: a list quietly narrowed by a
 * filter nobody can see is how somebody concludes their leads have vanished.
 * Built from the same predicate as the count above, so the badge and the chips
 * always describe the same set.
 */
function describeActiveFilters(
  filter: LeadFilter,
  lookups: { categories?: { id: number; name: string }[]; counties?: { id: number; name: string }[] },
): { key: string; label: string }[] {
  const name = (list: { id: number; name: string }[] | undefined, id: number) =>
    list?.find((entry) => entry.id === id)?.name ?? `#${id}`;

  return Object.entries(filter)
    .filter(isActiveFilterEntry)
    .map(([key, value]) => {
      const label = FILTER_LABELS[key] ?? key;

      let rendered: string;
      if (key === 'categoryIds' && Array.isArray(value)) {
        rendered = (value as number[]).map((id) => name(lookups.categories, id)).join(', ');
      } else if ((key === 'countyIds' || key === 'serviceCountyIds') && Array.isArray(value)) {
        const ids = value as number[];
        rendered =
          ids.length > 2
            ? `${ids.length} selected`
            : ids.map((id) => name(lookups.counties, id)).join(', ');
      } else if(key==='opportunityTypes' && Array.isArray(value)) {
        rendered=value.map(v=>OPPORTUNITY_LABELS[v as keyof typeof OPPORTUNITY_LABELS]).join(', ');
      } else if(key==='improvementCategories' && Array.isArray(value)) {
        rendered=value.map(v=>IMPROVEMENT_LABELS[v as keyof typeof IMPROVEMENT_LABELS]).join(', ');
      } else if (Array.isArray(value)) {
        rendered = value.length > 2 ? `${value.length} selected` : value.join(', ');
      } else if (typeof value === 'boolean') {
        // The label already reads as the assertion, so "yes" would be noise.
        rendered = value ? '' : 'no';
      } else {
        rendered = String(value);
      }

      return { key, label: rendered ? `${label}: ${rendered}` : label };
    });
}

function SaveFilterDialog({
  open,
  onOpenChange,
  filter,
  sortBy,
  sortDir,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filter: LeadFilter;
  sortBy: string;
  sortDir: 'asc' | 'desc';
}) {
  const [name, setName] = React.useState('');
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: () => api.createSavedFilter({ name, filter, sortBy, sortDir }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-filters'] });
      toast.success(`Saved “${name}”`);
      setName('');
      onOpenChange(false);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Save this filter</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Field label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Clark County roofers with no website"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) save.mutate();
              }}
            />
          </Field>
          <Card className="mt-3">
            <CardBody className="text-2xs text-muted-foreground">
              <Label>Saved criteria</Label>
              <pre className="mt-1 whitespace-pre-wrap font-mono">{JSON.stringify(filter, null, 2)}</pre>
            </CardBody>
          </Card>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={!name.trim()} loading={save.isPending} onClick={() => save.mutate()}>
            Save filter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
